/**
 * @file SellerLegalBlock.tsx
 * @description Сведения о продавце — как строка выглядит в подвале (phase19.16
 * Р1; SB19, SB59, NEW-1; uiux-design-r1 §2 «Всегда в подвале», §4
 * «Продавец»). Только показ: правка — в «Настройки → Реквизиты». Не
 * заполнено — красная полоса с перечнем полей и «Заполнить реквизиты →»:
 * без этих сведений опубликовать вид сайта нельзя.
 *
 * Приёмка Р2а (SB115, SB118): вкладка «Подвал» передаёт фактический состав
 * ссылок платформы в порядке владельца (`links`); скрытая из-за пункта меню
 * подвала — зачёркнута с пометкой. Без `links` — общий перечень.
 */

import { Lock } from "lucide-react";
import { Link } from "react-router";

import { Button } from "../ui/button";
import { ListError } from "../ui/ListStates";
import { NoticeStrip } from "../ui/NoticeStrip";
import { Skeleton } from "../ui/skeleton";
import { useCurrentContext } from "../../auth/hooks";
import { useSellerInfo, type SellerInfoState } from "../../hooks/site-view/useSellerInfo";

/** Ссылка платформы в перечне: подпись и почему её нет на сайте (выключена, дубль меню — SB115, SB118); null — есть. */
export interface SellerLegalLink {
  key: string;
  label: string;
  note: string | null;
}

interface Props {
  shopEmail: string | null;
  /** Фактический состав ссылок нижней полосы; не задан — общий перечень. */
  links?: readonly SellerLegalLink[];
  /** Подпись «Источник: …» (на экране «Настройки сайта»). */
  showSource?: boolean;
}

/** Блок с загрузкой сведений внутри (вкладка «Подвал»). */
export function SellerLegalBlock(props: Props) {
  const seller = useSellerInfo(props.shopEmail);
  return <SellerLegalView {...props} seller={seller} />;
}

/** Блок по уже загруженным сведениям (экран «Настройки сайта» помечает раздел ✕). */
export function SellerLegalView({ shopEmail, showSource = false, links, seller }: Props & { seller: SellerInfoState }) {
  const slug = useCurrentContext()?.shop?.slug ?? "";
  const requisites = `/shop/${slug}/settings/legal-profiles`;

  if (seller.error) {
    return <ListError message="Не удалось загрузить сведения о продавце. На сайте остаются прежние." onRetry={seller.reload} />;
  }
  if (!seller.info) return <Skeleton className="h-28 rounded-xl" aria-label="Загрузка сведений о продавце" />;
  const { profile, missing, ogrnLabel, noDefault } = seller.info;
  const line1 = [
    profile?.legalName || "наименование —",
    `${ogrnLabel} ${profile?.ogrn || "—"}`,
    `ИНН ${profile?.inn || "—"}`,
    profile?.legalAddress || "адрес —",
  ].join(" · ");
  const line2 = [shopEmail || "почта —", profile?.phone || "телефон —"].join(" · ");

  return (
    <div className="flex flex-col gap-3 rounded-xl bg-surface-lowest p-4 [box-shadow:inset_0_0_0_1px_var(--border)]">
      <div className="flex items-center gap-2">
        <Lock aria-hidden className="size-4 text-on-surface-variant" />
        <span className="type-card-title text-on-surface">Сведения о продавце</span>
      </div>
      {noDefault ? (
        <NoticeStrip tone="error">Выберите основной профиль в «Реквизитах» — его сведения будут на сайте.</NoticeStrip>
      ) : missing.length > 0 ? (
        <NoticeStrip tone="error">
          Не заполнены: {missing.join(", ")}. Без сведений о продавце опубликовать вид сайта нельзя: по закону они должны
          быть на каждой странице.
        </NoticeStrip>
      ) : null}
      <div className="type-body flex flex-col gap-1 text-on-surface">
        <span className="break-words">{line1}</span>
        <span className="break-words">{line2}</span>
        {links ? (
          <ul aria-label="Ссылки платформы" className="flex flex-wrap gap-x-2 text-on-surface-variant">
            {links.map((link) => (
              <li key={link.key}>
                <span className={link.note ? "line-through" : undefined}>{link.label}</span>
                {link.note ? <span className="type-caption"> ({link.note})</span> : null}
              </li>
            ))}
          </ul>
        ) : (
          <span className="text-on-surface-variant">Публичная оферта · Политика обработки ПДн · Возврат · Доставка и оплата</span>
        )}
      </div>
      <p className="type-caption text-on-surface-variant">
        По закону — на каждой странице. Убрать или сделать мельче нельзя — строку ставит платформа.
        {showSource ? " Источник: основной профиль в «Настройки → Реквизиты»; почта — из «Настройки → Магазин»." : ""}
      </p>
      <Button asChild variant="outline" className="self-start">
        <Link to={requisites}>{missing.length > 0 ? "Заполнить реквизиты →" : "Изменить в «Реквизитах» →"}</Link>
      </Button>
    </div>
  );
}
